import { CellError, getCellErrorForApiError } from '../constants/ErrorMessages';
import type { ValidationError } from '../types/journal';

/**
 * Errors where a second attempt cannot change the answer.
 *
 * Everything absent from this set is worth one retry — chiefly `NETWORK_ERROR`
 * and `SERVER_ERROR`, the transient pair a workbook meets constantly while
 * recalculating thousands of cells, plus anything unrecognised.
 *
 * `TIMEOUT` is here despite being transient: the request was already abandoned
 * after 60 seconds, so retrying costs the user two minutes on one cell while
 * the backend is most likely still working on the first attempt.
 */
const NON_RETRYABLE_CELL_ERRORS: ReadonlySet<string> = new Set([
  CellError.SEAT_LIMIT,
  CellError.NO_SUBSCRIPTION,
  CellError.SUBSCRIPTION_EXPIRED,
  CellError.TRIAL_EXPIRED,
  CellError.ERP_MISMATCH,
  CellError.SUBSCRIPTION_ISSUE,
  CellError.AUTH_FAILED,
  CellError.ACCESS_DENIED,
  CellError.DEMO_DISABLED,
  CellError.NO_ERP_CONNECTION,
  CellError.DPA_NOT_ACCEPTED,
  CellError.TIMEOUT,
  // The backend now paces itself against the ERP budget, so a 429 means the
  // limit is genuinely reached. Retrying adds load to make that worse.
  CellError.RATE_LIMITED,
]);

/**
 * Refusals whose backend message is written for the pane: shown as sent, not
 * reduced to a cell error, and never retried, since the same request gets the
 * same answer. A refused journal send names why (not submitted, own journal,
 * the ERP's own reason for a line), and that reason is the whole point of the message.
 */
const PASSTHROUGH_ERROR_CODES = new Set([
  'JOURNAL_APPROVAL_REQUIRED',
  'JOURNAL_INVALID',
  'ERP_REJECTED',
  'ERP_UNAVAILABLE',
  'JOURNAL_APPROVAL_IN_ERP',
  'JOURNAL_PUSH_IN_PROGRESS',
  'JOURNAL_BEING_DISCARDED',
  'JOURNAL_ERP_CONNECTION_CHANGED',
  'JOURNAL_POSTED_IN_ERP',
  'NO_DRAFT',
  'JOURNAL_POST_DENIED',
  'JOURNAL_PUSH_DENIED',
  'JOURNAL_POST_POLICY_DENIED',
  'JOURNAL_DISABLED',
  'JOURNAL_TEMPLATE_INACTIVE',
  'JOURNAL_CONNECTION_INACTIVE',
]);

/**
 * A journal write is never sent twice by the transport, whatever came back: the ERP may still be
 * working on the first, and only the user should decide to try again.
 */
function isJournalWrite(method: string, endpoint: string): boolean {
  return method !== 'GET' && endpoint.startsWith('/journal/');
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  /** The backend's error code, when it sent one. */
  code?: string;
  message?: string;
  status?: number;
  /** The lines a refused journal send failed on, as the backend listed them. */
  validationErrors?: ValidationError[];
}

type ApiFailure = Error & { code?: string; status?: number; minutesRemaining?: number; validationErrors?: ValidationError[] };

export interface RequestOptions {
  headers?: Record<string, string>;
  timeout?: number;
}

export class ApiService {
  private static readonly BASE_URL = process.env.API_BASE_URL;
  
  private static readonly DEFAULT_TIMEOUT = 60000; // 60 seconds for D365 queries

  /**
   * Total attempts, not retries: 2 means one retry.
   *
   * A dropped connection or a gateway blip fails a cell that would have
   * succeeded a second later, and a workbook recalculating thousands of cells
   * meets those constantly. What must *not* be retried is a timeout — at a
   * 60s budget a second attempt costs the user two minutes on one cell, and
   * the backend is very likely still working on the first. `isNonRetryableError`
   * draws that line; this number only says how many times to try.
   */
  private static readonly RETRY_ATTEMPTS = 2;
  private static readonly RETRY_DELAY = 1000; // 1 second

  /**
   * Make GET request to API
   */
  async get<T = unknown>(endpoint: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    return this.makeRequest('GET', endpoint, undefined, options);
  }

  /**
   * Make POST request to API
   */
  async post<T = unknown>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    return this.makeRequest('POST', endpoint, data, options);
  }

  /**
   * Make PUT request to API
   */
  async put<T = unknown>(endpoint: string, data?: unknown, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    return this.makeRequest('PUT', endpoint, data, options);
  }

  /**
   * Make DELETE request to API
   */
  async delete<T = unknown>(endpoint: string, options: RequestOptions = {}): Promise<ApiResponse<T>> {
    return this.makeRequest('DELETE', endpoint, undefined, options);
  }

  /**
   * Make request with retry logic and error handling
   */
  private async makeRequest<T>(
    method: string,
    endpoint: string,
    data?: unknown,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {

    const url = `${ApiService.BASE_URL}${endpoint}`;
    const timeout = options.timeout || ApiService.DEFAULT_TIMEOUT;
    const neverRetry = isJournalWrite(method, endpoint);

    let lastError: Error = new Error('Request failed');

    const attemptRequest = async (attempt: number): Promise<ApiResponse<T> | null> => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      try {
        const requestConfig: RequestInit = {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...options.headers
          },
          body: data ? JSON.stringify(data) : undefined,
          signal: controller.signal
        };

        // The timeout runs until the body is read, not only until the headers arrive: a body that
        // stalls must end the request too, or whoever waits on it waits for good.
        const response = await fetch(url, requestConfig);

        // Handle HTTP errors
        if (!response.ok) {
          let errorData: { error?: string; code?: string; minutesRemaining?: number; validationErrors?: unknown } = {};
          try {
            errorData = await response.json();
          } catch {
            // If JSON parsing fails, use status-based messages
          }

          if (errorData.error) {
            const apiError: ApiFailure = new Error(errorData.error);
            apiError.code = errorData.code;
            apiError.minutesRemaining = errorData.minutesRemaining;
            apiError.status = response.status;
            if (Array.isArray(errorData.validationErrors)) apiError.validationErrors = errorData.validationErrors as ValidationError[];
            throw apiError;
          }

          const httpError: Error & { status: number } = Object.assign(
            response.status === 401 ? new Error('Unauthorized - invalid credentials')
              : response.status === 403 ? new Error('Forbidden - insufficient permissions')
              : response.status === 429 ? new Error('Rate limit exceeded - too many requests')
              : response.status >= 500 ? new Error('Server error - please try again later')
              : new Error(`HTTP ${response.status}: ${response.statusText}`),
            { status: response.status }
          );
          throw httpError;
        }

        const result = await response.json();

        return {
          success: true,
          data: result.data || result,
          message: result.message
        };

      } catch (error) {
        lastError = error as Error;
        console.warn(`API request attempt ${attempt} failed:`, error);

        if (neverRetry || this.isNonRetryableError(error as Error)) {
          return null; // signal to stop retrying
        }

        if (attempt < ApiService.RETRY_ATTEMPTS) {
          await this.delay(ApiService.RETRY_DELAY * Math.pow(2, attempt - 1));
        }

        throw error; // signal to continue retrying
      } finally {
        clearTimeout(timeoutId);
      }
    };

    // Retry logic using recursion to avoid await-in-loop
    const tryAttempt = async (attempt: number): Promise<ApiResponse<T>> => {
      if (attempt > ApiService.RETRY_ATTEMPTS) {
        console.error('API request failed after all retry attempts:', lastError);
        return this.failure(lastError);
      }

      try {
        const result = await attemptRequest(attempt);
        if (result) return result;
        // non-retryable error - stop
        console.error('API request failed after all retry attempts:', lastError);
        return this.failure(lastError);
      } catch {
        return tryAttempt(attempt + 1);
      }
    };

    return tryAttempt(1);
  }

  private failure<T>(error: Error): ApiResponse<T> {
    const { code, status, validationErrors } = error as ApiFailure;
    const failure: ApiResponse<T> = { success: false, error: this.getErrorMessage(error), code, status };
    if (validationErrors) failure.validationErrors = validationErrors;
    return failure;
  }

  /**
   * Check if error should not be retried
   */
  private isNonRetryableError(error: Error): boolean {
    const message = error.message.toLowerCase();
    const { code, status } = error as Error & { code?: string; status?: number };

    if (code && PASSTHROUGH_ERROR_CODES.has(code)) return true;

    // Classified by the same mapper that produces the cell text, so the two
    // cannot drift. Duplicating the string matching here is how a licence
    // error, recognised for display but not for retry, ended up being sent
    // twice.
    if (NON_RETRYABLE_CELL_ERRORS.has(getCellErrorForApiError({ message: error.message, code, status }))) {
      return true;
    }

    // Not distinct cell errors, but still definitive: the request itself is
    // wrong, so sending it again changes nothing.
    if (message.includes('bad request') || message.includes('invalid')) {
      return true;
    }

    return false;
  }

  /**
   * Get user-friendly error message
   */
  private getErrorMessage(error: Error): string {
    const { code, status } = error as Error & { code?: string; status?: number };
    if (code && PASSTHROUGH_ERROR_CODES.has(code)) return error.message;
    return getCellErrorForApiError({ message: error.message, code, status });
  }

  /**
   * Delay execution for specified milliseconds
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.get('/health', { timeout: 5000 });
      return response.success;
    } catch {
      return false;
    }
  }
}